import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Toggle } from '../ui/Toggle';
import { Spinner } from '../ui/Spinner';
import { Textarea } from '../ui/Textarea';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useToast } from '../../hooks/useToast';
import type { useProject } from '../../hooks/useProject';
import { useExportStage } from '../../hooks/useExportStage';
import { BOOK_FONTS, ensureBookFontsRegistered } from '../../utils/bookFonts';
import { getCanvasAspect, parseSpreadElements } from '../../utils/spreadGeometry';
import { StaticSpread } from '../compose/SpreadCanvas';

type ProjectHookReturn = ReturnType<typeof useProject>;

interface ExportStageProps {
  projectHook: ProjectHookReturn;
}

export function ExportStage({ projectHook }: ExportStageProps) {
  // The cover preview renders the composed cover spread in the book font.
  ensureBookFontsRegistered();

  const {
    project,
    spreads,
    format,
    formatPreset,
    generating,
    pdfUrl,
    placementWarnings,
    buildWarnings,
    generatePdf,
    downloadPdf,
    goToStage,
  } = useExportStage(projectHook);
  const { updateProject } = projectHook;
  const { showToast } = useToast();

  const [includePageNumbers, setIncludePageNumbers] = useState(true);
  // A composed back cover ships by default — the toggle exists to leave
  // it out, not to remember to put it back in.
  const [includeBackCover, setIncludeBackCover] = useState(() => {
    const back = (project?.spreads ?? []).find((s) => s.kind === 'back');
    return parseSpreadElements(back?.elements ?? null).length > 0;
  });
  const [blankPageAfterCover, setBlankPageAfterCover] = useState(
    !!project?.blank_page_after_cover,
  );
  const handleBlankPageAfterCover = (v: boolean) => {
    setBlankPageAfterCover(v);
    void updateProject({ blank_page_after_cover: v });
  };
  const [includeDedication, setIncludeDedication] = useState(
    !!project?.dedication_text,
  );
  const [dedicationText, setDedicationText] = useState(project?.dedication_text || '');
  const { status: dedicationSaveStatus } = useAutoSave(dedicationText, async (text) => {
    await updateProject({ dedication_text: text || null });
  });
  useEffect(() => {
    if (dedicationSaveStatus === 'error') {
      showToast('Auto-save failed — your latest edit may not be saved', 'error');
    }
  }, [dedicationSaveStatus, showToast]);

  if (!project) return null;

  const interiorSpreads = spreads.filter((s) => s.kind === 'spread');
  const bookPageCount = interiorSpreads.length * 2;

  const bookFont = {
    family:
      BOOK_FONTS.find((f) => f.id === project.font_family)?.cssFamily ??
      BOOK_FONTS[0].cssFamily,
    weight: project.font_weight_style === 'bold' ? 700 : 400,
  };
  const coverSpread = spreads.find((s) => s.kind === 'cover') ?? null;

  // In a double-sided print, a spread stays on facing pages only when the
  // front matter (cover + dedication + blank) has an odd page count.
  const frontMatterPages =
    1 + (includeDedication && dedicationText ? 1 : 0) + (blankPageAfterCover ? 1 : 0);
  const spreadsStraddle = interiorSpreads.length > 0 && frontMatterPages % 2 === 0;

  // The stored PDF predates the latest canvas edits.
  const pdfOutdated =
    !!pdfUrl &&
    !!project.pdf_generated_at &&
    spreads.some((s) => s.updated_at > project.pdf_generated_at!);

  const handleGeneratePdf = () => {
    void generatePdf({ includePageNumbers, includeBackCover, blankPageAfterCover, includeDedication });
  };

  return (
    <div className="export-stage">
      <header className="stage-head">
        <div className="stage-head__eyebrow">
          <span className="step-of">step 4 of 4</span> · export
        </div>
        <h1 className="stage-head__title">
          Print, share, or <em>tuck it under the pillow</em>.
        </h1>
        <p className="stage-head__desc">
          A few finishing touches, then we&rsquo;ll generate a print-ready PDF that matches
          your spreads exactly — every picture and text block right where you placed it.
        </p>
      </header>

      <div className="export">
        {/* Left: the composed cover spread, rendered exactly as the PDF
            will (same StaticSpread as the Preview overlay — not the
            legacy cover_image_r2 + title overlay). */}
        <div className="export__preview-stack">
          <div className="export__book export__book--spread">
            <div className="spread-wrap">
              <StaticSpread
                spread={{
                  id: coverSpread?.id ?? 'cover-preview',
                  kind: 'cover',
                  elements: parseSpreadElements(coverSpread?.elements ?? null),
                }}
                aspect={getCanvasAspect(project.format_preset, 'cover')}
                bookFont={bookFont}
                formatPreset={project.format_preset}
                fallbackTitle={project.title}
              />
            </div>
          </div>
          <div className="export__preview-caption">
            <span className="export__preview-label">
              {formatPreset
                ? `${formatPreset.label} · ${formatPreset.description}`
                : format}
            </span>
          </div>
        </div>

        {/* Right: options + summary */}
        <div>
          <div className="section" style={{ marginTop: 0 }}>
            <div className="section__title">Finishing touches</div>
            <div className="section__sub">
              Switch on the bits you&rsquo;d like included in the printable PDF.
            </div>
            <div className="export__opts">
              <div className="opt-row">
                <div className="opt-row__label">
                  <div className="opt-row__title">Page numbers</div>
                  <div className="opt-row__sub">Centered along the bottom edge.</div>
                </div>
                <Toggle on={includePageNumbers} onChange={setIncludePageNumbers} />
              </div>
              <div className="opt-row">
                <div className="opt-row__label">
                  <div className="opt-row__title">Back cover</div>
                  <div className="opt-row__sub">Include the back-cover spread you composed.</div>
                </div>
                <Toggle on={includeBackCover} onChange={setIncludeBackCover} />
              </div>
              <div className="opt-row">
                <div className="opt-row__label">
                  <div className="opt-row__title">Blank page after cover</div>
                  <div className="opt-row__sub">Insert a blank page between the cover and the story.</div>
                </div>
                <Toggle on={blankPageAfterCover} onChange={handleBlankPageAfterCover} />
              </div>
              <div className="opt-row">
                <div className="opt-row__label">
                  <div className="opt-row__title">Dedication page</div>
                  <div className="opt-row__sub">A quiet page right after the cover.</div>
                </div>
                <Toggle on={includeDedication} onChange={setIncludeDedication} />
              </div>
              {includeDedication && (
                <Textarea
                  label="Dedication text"
                  value={dedicationText}
                  onChange={(e) => setDedicationText(e.target.value)}
                  placeholder="For my little one, who loves bedtime stories..."
                />
              )}
              {spreadsStraddle && (
                <div className="export__parity-hint" data-testid="parity-hint">
                  With these front pages, each story spread will straddle a
                  page turn when printed double-sided. Toggle &ldquo;Blank
                  page after cover&rdquo; to keep spreads on facing pages.
                </div>
              )}
            </div>
          </div>

          {(placementWarnings.length > 0 || buildWarnings.length > 0) && (
            <div className="section">
              <div className="section__title">Print check</div>
              <div className="section__sub">
                Heads-up only — you can always export. These are spots a
                print shop&rsquo;s trimming or binding could affect.
              </div>
              <ul className="export__preflight" data-testid="print-check">
                {placementWarnings.map((w) => (
                  <li key={w.id} className="export__preflight-item">
                    {w.message}
                  </li>
                ))}
                {buildWarnings.map((message) => (
                  <li key={message} className="export__preflight-item">
                    {message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="section">
            <div className="rail-card">
              <div className="rail-card__title">Ready to print</div>
              <div className="kv">
                <div className="kv__k">Title</div>
                <div className="kv__v">{project.title || '—'}</div>
              </div>
              <div className="kv">
                <div className="kv__k">Spreads</div>
                <div className="kv__v">
                  {interiorSpreads.length} ({bookPageCount} pages)
                </div>
              </div>
              <div className="kv">
                <div className="kv__k">Format</div>
                <div className="kv__v export__format-row">
                  <span>
                    {formatPreset
                      ? `${formatPreset.label} · ${formatPreset.width}" × ${formatPreset.height}"`
                      : format}
                  </span>
                  <button
                    type="button"
                    className="export__edit-format"
                    onClick={() => goToStage('define')}
                  >
                    Edit on Define
                  </button>
                </div>
              </div>

              {generating && (
                <div className="export__generating">
                  <Spinner size="sm" />
                  <span>Generating your book…</span>
                </div>
              )}

              {pdfUrl && !generating && (
                <div className="export__ready">
                  <span className="export__ready-label">
                    {pdfOutdated ? 'PDF outdated' : 'PDF ready'}
                  </span>
                  <Button variant="secondary" onClick={downloadPdf}>
                    Download PDF
                  </Button>
                </div>
              )}
              {pdfOutdated && !generating && (
                <div className="export__stale-hint" data-testid="pdf-outdated">
                  The book changed since this PDF was generated — regenerate
                  to include your latest edits.
                </div>
              )}

              <div className="export__print-note">
                The PDF exports at exact trim size in RGB color — your print
                shop converts it to CMYK (vivid blues and greens can shift
                slightly) and trims 2–3&nbsp;mm at the edges.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="stage-foot">
        <Button
          variant="ghost"
          onClick={() => goToStage('compose')}
        >
          &#8592; Compose
        </Button>
        <div className="stage-foot__spacer" />
        <Button
          variant="accent"
          onClick={handleGeneratePdf}
          loading={generating}
          disabled={generating}
        >
          {generating ? 'Generating…' : pdfUrl ? 'Regenerate PDF' : 'Export PDF'}
        </Button>
      </div>
    </div>
  );
}
